/**
 * AdminActivityPage
 * Owner-only view of community activity: who has created an account
 * (emails masked for privacy) and which drawings are getting marked
 * (counts only — never who marked what).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Users, CheckCircle2, RefreshCw, Loader2 } from 'lucide-react';
import { getAdminActivity, type AdminActivity } from '@/services/supabase-storage';

const MONO = { fontFamily: 'FK Grotesk Mono, monospace' } as const;

/** The reaction "mark" — a filled dot, sized/colored via className like a lucide icon. */
function Dot({ className = '' }: { className?: string }) {
  return (
    <span className={`inline-flex items-center justify-center ${className}`} aria-hidden="true">
      <span className="block w-1/2 h-1/2 rounded-full bg-current" />
    </span>
  );
}

function formatDate(raw?: string): string {
  if (!raw) return '—';
  // Postgres timestamps arrive as "2026-06-16 15:18:58.23" (space, no tz).
  const d = new Date(String(raw).replace(' ', 'T'));
  if (isNaN(d.getTime())) return String(raw).slice(0, 10);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function AdminActivityPage() {
  const [data, setData] = useState<AdminActivity | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      setError(null);
      setData(await getAdminActivity());
    } catch (e: any) {
      setError(e?.message || 'Could not load activity.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    if (!data) return [];
    return [
      { label: 'Accounts', value: data.accountCount, icon: Users },
      { label: 'Verified', value: data.verifiedCount, icon: CheckCircle2 },
      { label: 'Marked drawings', value: data.heartedCount, icon: Dot },
      { label: 'Total marks', value: data.totalHearts, icon: Dot },
    ];
  }, [data]);

  return (
    <div className="container mx-auto px-4 py-12 max-w-5xl">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-medium" style={MONO}>Activity</h1>
        <button
          onClick={() => load(true)}
          disabled={refreshing || loading}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
          style={MONO}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-muted-foreground animate-spin mb-3" />
          <p className="text-muted-foreground text-sm" style={MONO}>Loading activity…</p>
        </div>
      ) : error ? (
        <div className="bg-card border border-border rounded-lg p-6 text-center">
          <p className="text-muted-foreground mb-3" style={MONO}>{error}</p>
          <button onClick={() => load(true)} className="text-sm text-foreground underline" style={MONO}>Try again</button>
        </div>
      ) : data ? (
        <div className="space-y-12">
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {summary.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label} className="bg-card border border-border rounded-lg p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-muted-foreground mb-1 text-xs" style={MONO}>{s.label}</div>
                      <div className="text-foreground text-2xl font-semibold" style={MONO}>{s.value}</div>
                    </div>
                    <Icon className="w-5 h-5 text-muted-foreground/60" aria-hidden="true" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Accounts */}
          <section>
            <h2 className="text-lg font-medium mb-1" style={MONO}>Accounts</h2>
            <p className="text-muted-foreground/70 text-xs mb-4" style={MONO}>
              People who claimed a name. Emails are masked for privacy.
            </p>
            {data.accounts.length === 0 ? (
              <p className="text-muted-foreground text-sm" style={MONO}>No accounts yet.</p>
            ) : (
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <table className="w-full text-sm" style={MONO}>
                  <thead>
                    <tr className="border-b border-border text-muted-foreground/70 text-left text-xs">
                      <th className="px-4 py-3 font-normal">Name</th>
                      <th className="px-4 py-3 font-normal">Email</th>
                      <th className="px-4 py-3 font-normal text-center">Verified</th>
                      <th className="px-4 py-3 font-normal text-center">Drawings</th>
                      <th className="px-4 py-3 font-normal text-right">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.accounts.map((a, i) => (
                      <tr key={a.alias + i} className="border-b border-border/50 last:border-0">
                        <td className="px-4 py-3 text-foreground">{a.alias}</td>
                        <td className="px-4 py-3 text-muted-foreground">{a.emailMasked}</td>
                        <td className="px-4 py-3 text-center">
                          {a.verified
                            ? <CheckCircle2 className="w-4 h-4 text-green-500 inline" aria-label="verified" />
                            : <span className="text-muted-foreground/40">—</span>}
                        </td>
                        <td className="px-4 py-3 text-center text-muted-foreground">{a.drawings}</td>
                        <td className="px-4 py-3 text-right text-muted-foreground/70">{formatDate(a.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Most hearted */}
          <section>
            <h2 className="text-lg font-medium mb-1" style={MONO}>Most marked</h2>
            <p className="text-muted-foreground/70 text-xs mb-4" style={MONO}>
              Drawings people are marking, most-loved first.
            </p>
            {data.hearted.length === 0 ? (
              <p className="text-muted-foreground text-sm" style={MONO}>No marks yet.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {data.hearted.map((h) => (
                  <div key={h.id} className="bg-card border border-border rounded-lg overflow-hidden">
                    <div className="relative aspect-[5/7] bg-background">
                      {h.imageUrl ? (
                        <img src={h.imageUrl} alt={h.promptText || 'Drawing'} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground/40 text-xs" style={MONO}>no image</div>
                      )}
                      <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/70 text-white rounded-full px-2 py-0.5 text-xs" style={MONO}>
                        <Dot className="w-3 h-3" />
                        {h.heartCount}
                      </div>
                    </div>
                    <div className="p-3">
                      <div className="text-muted-foreground/60 text-[10px]" style={MONO}>
                        {h.promptNumber ? `#${String(h.promptNumber).padStart(4, '0')}` : '—'}
                      </div>
                      {h.promptText && (
                        <div className="text-foreground text-xs mt-1 line-clamp-2" style={MONO}>{h.promptText}</div>
                      )}
                      {h.artistName && (
                        <div className="text-muted-foreground/70 text-[10px] mt-1 truncate" style={MONO}>by {h.artistName}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      ) : null}
    </div>
  );
}

export default AdminActivityPage;
